import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent } from '../../../shared/utils/base.component';
import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { RADIO_YES_NO_OPTIONS } from '../../../shared/constants/static-data.constants';
import { SectionsService } from '../../../services/sections.service';
import { UserService } from '../../../services/user.service';
import { DropdownSelectComponent } from '@ppl/ui';
import { ReferralDataService } from '../../../services/referral-data.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ppl-payment-details',
  standalone: true,
  imports: [
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    NzGridModule,
    NzCardModule,
    NzRadioModule,
    NzDatePickerModule,
    CommonModule,
    NzToolTipModule,
    NzIconModule,
    NzInputModule,
    NzSelectModule,
    NzDividerModule,
    DropdownSelectComponent
  ],
  templateUrl: './payment-details.component.html',
  styleUrl: './payment-details.component.css',
})
export class PaymentDetailsComponent extends BaseComponent implements OnInit {
  @Input() mode = 'edit';
  paymentDetailsForm!: FormGroup;
  radioOptions = RADIO_YES_NO_OPTIONS;
  readonly directDepositValue = 'directDeposit';
  paymentOptions = [
    {
      label: 'Direct Deposit to a Bank Account or Debit Card',
      value: this.directDepositValue,
    }
  ];
  directDepositOptions = [
    { label: 'Bank Checking Account', value: 'checking' },
    { label: 'Bank Savings Account', value: 'savings' },
    { label: 'Third-party Money App', value: 'moneyApp' },
  ];
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private sectionService: SectionsService,
    private userService: UserService,
    private referralDataService: ReferralDataService
  ) {
    super();
  }

  ngOnInit(): void {
    this.paymentDetailsForm = this.fb.group({
      paymentMethod: [null],
      bankName: [{ value: '', disabled: true }],
      accountType: [{ value: '', disabled: true }],
      routingNumber: [{ value: '', disabled: true }],
      accountNumber: [{ value: '', disabled: true }],
      payStub: [null],
    });

    const program = this.userService.getSelectedProgram();
    const steps = this.sectionService.getSectionData(program?.program_code, '');
    this.secData = steps['payment_details'];

    const savedPaymentData = this.referralDataService.getReferralFormData().paymentDetailsData;
    if (savedPaymentData) {
      this.paymentDetailsForm.patchValue(this.normalizePaymentDetailsData(savedPaymentData));
    }

    this.updatePaymentMethodState(this.paymentDetailsForm.get('paymentMethod')?.value);
    this.updateDirectDepositFieldState(this.paymentDetailsForm.get('accountType')?.value);
    this.setupSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  get isDirectDepositSelected(): boolean {
    return this.paymentDetailsForm.get('paymentMethod')?.value === this.directDepositValue;
  }

  private setupSubscriptions(): void {
    const paymentMethodSub = this.paymentDetailsForm.get('paymentMethod')?.valueChanges.subscribe((value) => {
      this.updatePaymentMethodState(value);
      this.persistFormState();
      this.refreshValidationErrors();
    });
    if (paymentMethodSub) {
      this.subscriptions.push(paymentMethodSub);
    }

    const accountTypeSub = this.paymentDetailsForm.get('accountType')?.valueChanges.subscribe((value) => {
      this.updateDirectDepositFieldState(value);
      this.persistFormState();
      this.refreshValidationErrors();
    });
    if (accountTypeSub) {
      this.subscriptions.push(accountTypeSub);
    }

    const formSub = this.paymentDetailsForm.valueChanges.subscribe(() => {
      this.persistFormState();
      this.refreshValidationErrors();
    });
    this.subscriptions.push(formSub);

    const nextSub = this.referralDataService.nextClicked.subscribe((value: any) => {
      if (value === 'payment_details') {
        this.paymentDetailsForm.markAllAsTouched();
        this.validationErrors = this.buildValidationErrors();
        this.referralDataService.setError(this.validationErrors);
      }
    });
    this.subscriptions.push(nextSub);

    const previousSub = this.referralDataService.previousClicked.subscribe((value: any) => {
      if (value === 'payment_details') {
        this.validationErrors = {};
      }
    });
    this.subscriptions.push(previousSub);
  }

  private persistFormState(): void {
    const formValue = this.paymentDetailsForm.getRawValue();
    this.referralDataService.setReferralFormData('paymentDetailsData', formValue);
    this.referralDataService.setReferralFormData(
      'isPaymentFormUpdated',
      this.hasAnyValueInForm(this.paymentDetailsForm)
    );
  }

  private updatePaymentMethodState(paymentMethod: string | null): void {
    const accountTypeControl = this.paymentDetailsForm.get('accountType');
    if (!accountTypeControl) {
      return;
    }

    if (paymentMethod === this.directDepositValue) {
      accountTypeControl.enable({ emitEvent: false });
      accountTypeControl.setValidators([Validators.required]);
    } else {
      accountTypeControl.reset('', { emitEvent: false });
      accountTypeControl.clearValidators();
      accountTypeControl.disable({ emitEvent: false });
      this.resetDirectDepositFields();
    }

    accountTypeControl.updateValueAndValidity({ emitEvent: false });
  }

  private updateDirectDepositFieldState(accountType: string | null): void {
    const directDepositControls = ['bankName', 'routingNumber', 'accountNumber'];
    const controls = directDepositControls
      .map((controlName) => this.paymentDetailsForm.get(controlName))
      .filter((control): control is AbstractControl => control !== null);

    if (this.isDirectDepositSelected && !!accountType) {
      controls.forEach((control) => control.enable({ emitEvent: false }));
      this.paymentDetailsForm.get('bankName')?.setValidators([Validators.required]);
      this.paymentDetailsForm.get('routingNumber')?.setValidators([
        Validators.required,
        this.routingNumberValidator(),
      ]);
      this.paymentDetailsForm.get('accountNumber')?.setValidators([
        Validators.required,
        this.accountNumberValidator(),
      ]);
    } else {
      this.resetDirectDepositFields();
    }

    controls.forEach((control) => control.updateValueAndValidity({ emitEvent: false }));
  }

  private resetDirectDepositFields(): void {
    ['bankName', 'routingNumber', 'accountNumber'].forEach((controlName) => {
      const control = this.paymentDetailsForm.get(controlName);
      if (!control) {
        return;
      }
      control.reset('', { emitEvent: false });
      control.clearValidators();
      control.disable({ emitEvent: false });
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private buildValidationErrors(): Record<string, string> {
    const errors: Record<string, string> = {};
    const paymentMethod = this.paymentDetailsForm.get('paymentMethod');
    const accountType = this.paymentDetailsForm.get('accountType');
    const bankName = this.paymentDetailsForm.get('bankName');
    const routingNumber = this.paymentDetailsForm.get('routingNumber');
    const accountNumber = this.paymentDetailsForm.get('accountNumber');

    if (paymentMethod?.hasError('required')) {
      errors['paymentMethod'] = 'Payment Method is required.';
    }
    if (accountType?.enabled && accountType.hasError('required')) {
      errors['accountType'] = 'Please select one direct deposit option.';
    }
    if (bankName?.enabled && bankName.hasError('required')) {
      errors['bankName'] = 'Bank or Money App Name is required.';
    }
    if (routingNumber?.enabled) {
      if (routingNumber.hasError('required')) {
        errors['routingNumber'] = 'Routing Number is required.';
      } else if (routingNumber.hasError('routingNumberFormat')) {
        errors['routingNumber'] = 'Routing Number must be numeric and exactly 9 digits.';
      } else if (routingNumber.hasError('routingNumberChecksum')) {
        errors['routingNumber'] = 'Routing Number must pass ABA checksum validation.';
      }
    }
    if (accountNumber?.enabled) {
      if (accountNumber.hasError('required')) {
        errors['accountNumber'] = 'Account Number is required.';
      } else if (accountNumber.hasError('accountNumberFormat')) {
        errors['accountNumber'] = 'Account Number must be numeric and 6 to 17 digits.';
      }
    }

    return errors;
  }

  private refreshValidationErrors(): void {
    if (Object.keys(this.validationErrors).length > 0) {
      this.validationErrors = this.buildValidationErrors();
    }
  }

  private routingNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = `${control.value ?? ''}`.trim();
      if (!value) {
        return null;
      }
      if (!/^\d{9}$/.test(value)) {
        return { routingNumberFormat: true };
      }

      const digits = value.split('').map(Number);
      const checksum =
        (digits[0] + digits[3] + digits[6]) * 3 +
        (digits[1] + digits[4] + digits[7]) * 7 +
        (digits[2] + digits[5] + digits[8]);

      return checksum % 10 === 0 ? null : { routingNumberChecksum: true };
    };
  }

  private accountNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = `${control.value ?? ''}`.trim();
      if (!value) {
        return null;
      }
      return /^\d{6,17}$/.test(value) ? null : { accountNumberFormat: true };
    };
  }

  private normalizePaymentDetailsData(data: Record<string, any>): Record<string, any> {
    const normalizedData = { ...data };
    if (normalizedData.paymentMethod === 'bankAccnt' || normalizedData.paymentMethod === 'debitCard') {
      normalizedData.paymentMethod = this.directDepositValue;
    }
    return normalizedData;
  }
}
